import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GiSpy } from 'react-icons/gi'
import { HiMenu, HiX } from 'react-icons/hi'

interface LandingNavProps {
  onCTAClick: () => void
  onNavClick: (id: string) => void
}

export function LandingNav({ onCTAClick, onNavClick }: LandingNavProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNavClick = (id: string) => {
    onNavClick(id)
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <motion.nav
        className={`landing-nav ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="landing-nav-inner">
          <a href="/" className="landing-logo">
            <GiSpy className="landing-logo-icon" />
            <span>StatScout</span>
          </a>

          <div className="landing-nav-links">
            <span
              className="landing-nav-link"
              onClick={() => handleNavClick('features')}
            >
              Features
            </span>
            <span
              className="landing-nav-link"
              onClick={() => handleNavClick('pricing')}
            >
              Pricing
            </span>
            <span
              className="landing-nav-link"
              onClick={() => handleNavClick('faq')}
            >
              FAQ
            </span>
          </div>

          <div className="landing-nav-ctas">
            <span className="landing-nav-signin" onClick={onCTAClick}>
              Sign In
            </span>
            <button className="cta-button small" onClick={onCTAClick}>
              Start Free Trial
            </button>
          </div>

          <button
            className="landing-nav-toggle"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <HiMenu />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="landing-mobile-menu open"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              className="landing-mobile-menu-close"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <HiX />
            </button>

            <motion.span
              className="landing-mobile-menu-link"
              onClick={() => handleNavClick('features')}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Features
            </motion.span>
            <motion.span
              className="landing-mobile-menu-link"
              onClick={() => handleNavClick('pricing')}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              Pricing
            </motion.span>
            <motion.span
              className="landing-mobile-menu-link"
              onClick={() => handleNavClick('faq')}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              FAQ
            </motion.span>
            <motion.button
              className="cta-button"
              onClick={() => {
                setIsMobileMenuOpen(false)
                onCTAClick()
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              Start Free Trial
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
